export function mergeRuntimeCharacterStats(manifestVariants, runtimeVariants) {
  const runtime = new Map((Array.isArray(runtimeVariants) ? runtimeVariants : []).map(character => [String(character?.id || ''), character]))
  return (Array.isArray(manifestVariants) ? manifestVariants : []).map(character => {
    const source = runtime.get(String(character?.id || ''))
    if (!source) return character
    const stats = new Map((Array.isArray(source.skills) ? source.skills : []).map(skill => [String(skill?.id || skill?.trigger || ''), skill?.stats]))
    const skills = (Array.isArray(character?.skills) ? character.skills : []).map(skill => {
      const runtimeStats = stats.get(String(skill?.id || skill?.trigger || ''))
      return runtimeStats && typeof runtimeStats === 'object' ? { ...skill, stats: runtimeStats } : skill
    })
    return { ...character, skills }
  })
}

export function runtimeCharacter(selected, binding, auto = false) {
  if (!selected || typeof selected !== 'object') return null
  const skills = Array.isArray(selected.skills) ? selected.skills : []
  const patched = skills.some(skill => {
    const stats = skill?.stats
    return stats?.modified === true || stats?.resettable === true || String(stats?.patch_description || stats?.patchDescription || '').trim() !== ''
  })
  return {
    ...selected,
    binding: binding || null,
    patched,
    ability_modified: patched,
    auto: Boolean(auto),
  }
}

export function buildLiveSnapshot(store, selectedWallet, selectedStrategySignalsEnabled = false, scopedStrategySignals = null) {
  const rawCoins = Object.values(store?.coins || {})
  const rawMints = Object.values(store?.mints || {})
  const root = store?.state?.value ?? store?.state ?? {}
  const feedStats = publicFeedStats(store?.mintsStats || root?.mintsStats || {})
  const snapshotAtMs = Date.now()
  const coins = rawCoins.map(c => publicCoin(c, false, selectedStrategySignalsEnabled, snapshotAtMs, scopedStrategySignals)).slice(0, 200)
  const mints = rawMints.map(c => publicCoin(c, true, selectedStrategySignalsEnabled, snapshotAtMs, scopedStrategySignals)).slice(0, 200)
  const market = new Set([...rawCoins, ...rawMints].map(c => c?.mint).filter(Boolean))
  const inPlay = new Set(rawMints.map(c => c?.mint).filter(Boolean))
  const wallet = publicWallet(store, selectedWallet)
  const positionLifecycles = publicPositionLifecycles(root?.positionLifecycles, wallet?.public_key)
  const closedPositions = publicClosedPositions(positionLifecycles)
  return plainBridgeValue({
    snapshotAtMs,
    snapshot_at_ms: snapshotAtMs,
    user: publicUser(store?.user),
    wallets: Object.values(store?.wallets || {}).map(w => publicWallet(store, w)),
    selectedWallet: wallet,
    selected_wallet: wallet,
    pnlPeriod: 'day',
    pnl_period: 'day',
    strategySignalsEnabled: Boolean(selectedStrategySignalsEnabled),
    strategy_signals_enabled: Boolean(selectedStrategySignalsEnabled),
    controls: publicControls(store, selectedWallet, selectedStrategySignalsEnabled),
    battles: positionLifecycles,
    battleHistory: positionLifecycles,
    battle_history: positionLifecycles,
    battleCount: positionLifecycles.length,
    battle_count: positionLifecycles.length,
    positionLifecycles,
    position_lifecycles: positionLifecycles,
    marketTrades: publicMarketTrades(root?.recentMarketTrades, market),
    market_trades: publicMarketTrades(root?.recentMarketTrades, market),
    closedPositions,
    closed_positions: closedPositions,
    feedStats,
    feed_stats: feedStats,
    coins,
    mints,
    market_count: market.size,
    marketCount: market.size,
    in_play_count: inPlay.size,
    inPlayCount: inPlay.size,
    snapshotLimit: 200,
    snapshot_limit: 200,
    snapshotTruncated: rawCoins.length > 200 || rawMints.length > 200,
    snapshot_truncated: rawCoins.length > 200 || rawMints.length > 200,
    live: {
      marketCount: market.size,
      market_count: market.size,
      inPlayCount: inPlay.size,
      in_play_count: inPlay.size,
      feedStats,
      feed_stats: feedStats,
    },
    pnl: store?.pnl || {},
    pad: publicCoin(store?.pad || {}, false, selectedStrategySignalsEnabled, snapshotAtMs, scopedStrategySignals),
  })
}

function publicFeedStats(stats) {
  const visible = nonNegative(stats?.visible)
  const skipped = nonNegative(stats?.skipped)
  const total = nonNegative(stats?.total ?? visible + skipped)
  const ratioRaw = Number(stats?.ratio)
  const ratio = Number.isFinite(ratioRaw)
    ? Math.max(0, Math.min(1, ratioRaw))
    : (total > 0 ? visible / total : 0)
  return {
    visible,
    skipped,
    total,
    matched: nonNegative(stats?.matched),
    filtered: nonNegative(stats?.filtered),
    duplicates: nonNegative(stats?.duplicates),
    limited: nonNegative(stats?.limited),
    ratio,
    reasons: plainBridgeValue(stats?.reasons || {}),
  }
}

function publicUser(u) {
  return u ? { name: u.name || '', public_key: u.public_key || '', publicKey: u.public_key || '' } : null
}

function publicWallet(store, w) {
  if (!w) return null
  const p = 'day'
  const publicKey = w.public_key || w.publicKey || ''
  const pnlLamports = walletPnlValue(w, p)
  const pnlCostLamports = walletPnlCostValue(w, p)
  return {
    name: w.name || '',
    public_key: publicKey,
    publicKey,
    lamports: Number(w.lamports || 0),
    sol: Number(w.lamports || 0) / 1e9,
    pnl_lamports: pnlLamports,
    pnlLamports,
    pnl_cost_lamports: pnlCostLamports,
    pnlCostLamports,
    pnl_period: p,
    pnlPeriod: p,
    status: w.status || '',
  }
}

function publicControls(store, selectedWallet, selectedStrategySignalsEnabled) {
  const feed = store?.filter || store?.settings?.mints || {}
  const autotrade = store?.autotrade || store?.settings?.autotrade || {}
  const wallet = String(selectedWallet?.public_key || selectedWallet?.publicKey || '').trim()
  const owner = wallet && store?.trade?.owners?.[wallet] && typeof store.trade.owners[wallet] === 'object'
    ? store.trade.owners[wallet]
    : {}
  return {
    feed: {
      holders: nonNegative(feed.holders),
      minLiquidityLamports: nonNegative(feed.minLiquidityLamports ?? feed.liquidity),
      maxAgeMs: nonNegative(feed.maxAgeMs),
      twitter: Boolean(feed.twitter),
      website: Boolean(feed.website),
      devLeft: Boolean(feed.devLeft),
      throughPumpfun: Boolean(feed.throughPumpfun),
      showDuplicates: Boolean(feed.showDuplicates),
      status: String(feed.status || 'enabled'),
      sort: plainBridgeValue(feed.sort || { mode: 'score', dir: -1 }),
    },
    trade: {
      enabled: Boolean(owner.enabled),
      entryMode: publicEntryMode(owner, autotrade),
      entry_mode: publicEntryMode(owner, autotrade),
      pct: nonNegative(owner.pct),
      buyLamports: nonNegative(owner.buyLamports),
      takeProfitBps: nonNegative(owner.takeProfitBps),
      takeProfitLamports: nonNegative(owner.takeProfitLamports),
      stopLossBps: nonNegative(owner.stopLossBps),
      maxHoldMs: nonNegative(owner.maxHoldMs),
    },
    autotrade: {
      enabled: Boolean(autotrade.enabled) && wallet !== '' && String(autotrade.wallet || '') === wallet,
      wallet: String(autotrade.wallet || ''),
      buyLamports: nonNegative(autotrade.buyLamports),
      delayMs: nonNegative(autotrade.delayMs),
      takeProfitBps: nonNegative(autotrade.takeProfitBps),
      takeProfitLamports: nonNegative(autotrade.takeProfitLamports),
      stopLossBps: nonNegative(autotrade.stopLossBps),
      maxHoldMs: nonNegative(autotrade.maxHoldMs),
      singlePosition: autotrade.singlePosition == null ? true : Boolean(autotrade.singlePosition),
      autoStopMaxPositions: nonNegative(autotrade.autoStopMaxPositions),
      autoStopProfitLamports: nonNegative(autotrade.autoStopProfitLamports),
      autoStopLossLamports: nonNegative(autotrade.autoStopLossLamports),
      autoStopReason: String(autotrade.autoStopReason || ''),
    },
    strategy: {
      signalsEnabled: Boolean(selectedStrategySignalsEnabled),
      entryStrategyId: nonNegative(owner.entryStrategyId),
      entryStrategyVersion: nonNegative(owner.entryStrategyVersion),
      positionStrategyId: nonNegative(owner.positionStrategyId),
      positionStrategyVersion: nonNegative(owner.positionStrategyVersion),
    },
  }
}

function publicEntryMode(owner, autotrade) {
  const explicit = String(owner?.entryMode || owner?.entry_mode || '').trim().toLowerCase()
  if (explicit === 'pct' || explicit === 'amount') return explicit
  return nonNegative(owner?.buyLamports) > 0 || nonNegative(autotrade?.buyLamports) > 0 ? 'amount' : 'pct'
}

function publicCoin(c, inPlay = false, selectedStrategySignalsEnabled = false, snapshotAtMs = Date.now(), scopedStrategySignals = null) {
  const mint = String(c?.mint || '')
  const scoped = mint && scopedStrategySignals && typeof scopedStrategySignals === 'object' ? scopedStrategySignals[mint] : null
  const strategyRaw = scoped || c?.strategy || c?.strategyDecision || null
  const strategy = selectedStrategySignalsEnabled ? strategyRaw : null
  const d = strategy?.decision && typeof strategy.decision === 'object' ? strategy.decision : {}
  const action = String(strategy?.action || d?.action || '').toLowerCase()
  const signal = String(d?.status || d?.signal || strategy?.signal || (strategy?.status !== 'ok' && strategy?.status !== 'error' ? strategy?.status : '') || action || '').toLowerCase()
  const strategyFact = selectedStrategySignalsEnabled ? publicStrategy(strategy, d, action, signal) : null
  const position = plainBridgeValue(c?.position || null)
  const lamports = Number(c?.lamports || 0)
  const liquidityLamports = Number(c?.liquidity_lamports ?? c?.liquidityLamports ?? c?.poolLamports ?? c?.lamports ?? 0)
  const holdersCount = Number(c?.holders_count ?? c?.holdersCount ?? c?.holders ?? 0)
  const trades = Number(c?.trades ?? c?.trades_count ?? c?.tradesCount ?? 0)
  const score = Number(c?.score ?? d?.score ?? strategy?.score ?? 0)
  const createdAt = c?.created_at || c?.createdAt || ''
  const ageSeconds = canonicalAgeSeconds(c, createdAt, snapshotAtMs)
  const feedVisibleSeconds = firstNumber(c?.feed_visible_seconds, c?.feedVisibleSeconds, strategyRaw?.feedVisibleSeconds, strategyRaw?.feed_visible_seconds)
  const inFeedRaw = firstDefined(c?.in_feed, c?.inFeed, strategyRaw?.inFeed, strategyRaw?.in_feed)
  const inFeed = inFeedRaw == null ? Boolean(inPlay) : Boolean(inFeedRaw)
  const feedDisappeared = Boolean(firstDefined(c?.feed_disappeared, c?.feedDisappeared, strategyRaw?.feedDisappeared, strategyRaw?.feed_disappeared) ?? false)
  const feedFilterFailures = firstArray(c?.feed_filter_failures, c?.feedFilterFailures, strategyRaw?.feedFilterFailures, strategyRaw?.feed_filter_failures)
  const feedFilterPassedRaw = firstDefined(c?.feed_filter_passed, c?.feedFilterPassed, strategyRaw?.feedFilterPassed, strategyRaw?.feed_filter_passed)
  const feedFilterPassed = feedFilterPassedRaw == null ? inFeed && feedFilterFailures.length === 0 : Boolean(feedFilterPassedRaw)
  const inPosition = hasOpenPosition(position)
  const pnlLamports = coinPnlLamports(c, position)
  const pnlBasisLamports = Number(position?.spent_lamports ?? position?.spentLamports ?? 0)
  const devLeft = Boolean(c?.dev_left ?? c?.devLeft ?? false)
  const throughPumpfun = Boolean(c?.through_pumpfun ?? c?.throughPumpfun ?? String(c?.mint || '').endsWith('pump'))
  const deltas = publicDeltas(c, strategyRaw)
  const creatorReputation = plainBridgeValue(c?.creator_reputation ?? c?.creatorReputation ?? c?.reputation ?? strategyRaw?.creatorReputation ?? strategyRaw?.creator_reputation ?? null)
  return {
    mint: c?.mint || '',
    name: c?.name || '',
    symbol: c?.symbol || '',
    image: c?.image || '',
    lamports,
    liquidity_lamports: liquidityLamports,
    liquidityLamports,
    liquidity_sol: liquidityLamports / 1e9,
    liquiditySol: liquidityLamports / 1e9,
    pool_lamports: liquidityLamports,
    poolLamports: liquidityLamports,
    amount: c?.amount || '',
    pool_amount_raw: c?.amount || '',
    poolAmountRaw: c?.amount || '',
    holders_count: holdersCount,
    holdersCount,
    holders: holdersCount,
    trades,
    trades_count: trades,
    tradesCount: trades,
    score,
    strength: score,
    deltas,
    holders_delta_1s: deltas.s1.holders,
    holders_delta_3s: deltas.s3.holders,
    holders_delta_5s: deltas.s5.holders,
    holders_delta_10s: deltas.s10.holders,
    trades_delta_1s: deltas.s1.trades,
    trades_delta_3s: deltas.s3.trades,
    trades_delta_5s: deltas.s5.trades,
    trades_delta_10s: deltas.s10.trades,
    liquidity_sol_delta_1s: deltas.s1.liquiditySol,
    liquidity_sol_delta_3s: deltas.s3.liquiditySol,
    liquidity_sol_delta_5s: deltas.s5.liquiditySol,
    liquidity_sol_delta_10s: deltas.s10.liquiditySol,
    score_delta_1s: deltas.s1.score,
    score_delta_3s: deltas.s3.score,
    score_delta_5s: deltas.s5.score,
    score_delta_10s: deltas.s10.score,
    created_at: createdAt,
    createdAt,
    age_seconds: ageSeconds,
    ageSeconds,
    feed_visible_seconds: feedVisibleSeconds,
    feedVisibleSeconds,
    in_feed: inFeed,
    inFeed,
    feed_disappeared: feedDisappeared,
    feedDisappeared,
    feed_filter_passed: feedFilterPassed,
    feedFilterPassed,
    feed_filter_failures: feedFilterFailures,
    feedFilterFailures,
    owner: c?.owner || c?.creator || '',
    creator: c?.creator || c?.owner || '',
    creator_reputation: creatorReputation,
    creatorReputation,
    twitter: c?.twitter || '',
    website: c?.website || '',
    telegram: c?.telegram || '',
    dev_left: devLeft,
    devLeft,
    through_pumpfun: throughPumpfun,
    throughPumpfun,
    in_play: Boolean(inPlay),
    inPlay: Boolean(inPlay),
    live: Boolean(inPlay),
    strategy: strategyFact,
    strategy_signal: signal,
    strategySignal: signal,
    strategy_status: signal,
    strategyStatus: signal,
    strategy_action: action,
    strategyAction: action,
    strategy_reason: String(strategyFact?.reason || ''),
    strategyReason: String(strategyFact?.reason || ''),
    position,
    in_position: inPosition,
    inPosition,
    pnl_lamports: pnlLamports,
    pnlLamports,
    pnl_basis_lamports: pnlBasisLamports,
    pnlBasisLamports,
  }
}

function publicStrategy(strategy, decision, action, signal) {
  if (!strategy || typeof strategy !== 'object') return null
  const displayLabel = String(decision?.displayLabel ?? decision?.display_label ?? decision?.label ?? strategy?.displayLabel ?? strategy?.display_label ?? strategy?.label ?? '')
  const reason = String(strategy?.reason ?? decision?.reason ?? '')
  const status = String(strategy?.status ?? decision?.status ?? signal ?? '').toLowerCase()
  return plainBridgeValue({
    trigger: String(strategy?.trigger ?? decision?.trigger ?? ''),
    strategyId: nonNegative(strategy?.strategyId ?? strategy?.strategy_id ?? strategy?.id ?? decision?.strategyId ?? decision?.strategy_id),
    strategyVersion: nonNegative(strategy?.strategyVersion ?? strategy?.strategy_version ?? strategy?.version ?? decision?.strategyVersion ?? decision?.strategy_version),
    strategySignalId: String(strategy?.strategySignalId ?? strategy?.strategy_signal_id ?? strategy?.signalId ?? strategy?.signal_id ?? decision?.strategySignalId ?? decision?.strategy_signal_id ?? ''),
    action,
    signal,
    status,
    displayLabel,
    reason,
    decisionAt: String(strategy?.decisionAt ?? strategy?.decision_at ?? strategy?.created_at ?? strategy?.updated_at ?? decision?.decisionAt ?? decision?.decision_at ?? ''),
  })
}

function canonicalAgeSeconds(c, createdAt, snapshotAtMs) {
  const direct = c?.age_seconds ?? c?.ageSeconds
  if (direct != null && direct !== '') {
    const number = Number(direct)
    if (Number.isFinite(number)) return Math.max(0, number)
  }
  const createdMs = Date.parse(String(createdAt || ''))
  if (!Number.isFinite(createdMs)) return 0
  return Math.max(0, (snapshotAtMs - createdMs) / 1000)
}

function firstNumber(...values) {
  for (const value of values) {
    if (value == null || value === '') continue
    const number = Number(value)
    if (Number.isFinite(number)) return Math.max(0, number)
  }
  return 0
}

function firstDefined(...values) {
  for (const value of values) if (value != null) return value
  return undefined
}

function firstArray(...values) {
  for (const value of values) if (Array.isArray(value)) return value.map(String)
  return []
}

function nonNegative(value) {
  const number = Number(value)
  return Number.isFinite(number) && number >= 0 ? number : 0
}

function publicDeltas(c, strategy) {
  return {
    s1: deltaWindow(c, strategy, 1),
    s3: deltaWindow(c, strategy, 3),
    s5: deltaWindow(c, strategy, 5),
    s10: deltaWindow(c, strategy, 10),
  }
}

function deltaWindow(c, strategy, seconds) {
  return {
    holders: readDelta(c, strategy, 'holders', seconds),
    trades: readDelta(c, strategy, 'trades', seconds),
    liquiditySol: readDelta(c, strategy, 'liquiditySol', seconds),
    score: readDelta(c, strategy, 'score', seconds),
  }
}

function readDelta(c, strategy, metric, seconds) {
  const suffix = `${seconds}s`
  const snakeMetric = metric === 'liquiditySol' ? 'liquidity_sol' : metric
  const camelField = `${metric}Delta${seconds}s`
  const candidates = [
    c?.[camelField],
    c?.[`${metric}_delta_${suffix}`],
    c?.[`${snakeMetric}_delta_${suffix}`],
    c?.deltas?.[suffix]?.[metric],
    c?.deltas?.[`s${seconds}`]?.[metric],
    c?.history?.[suffix]?.[metric],
    strategy?.[camelField],
    strategy?.market?.[camelField],
    strategy?.decision?.[camelField],
  ]
  for (const value of candidates) {
    const n = Number(value)
    if (Number.isFinite(n)) return n
  }
  return 0
}

function hasOpenPosition(p) {
  return !!p && String(p?.stage || p?.Stage || '').trim().toUpperCase() !== 'FINAL' && Number(p?.token_amount || p?.tokenAmount || 0) > 0
}

function coinPnlLamports(c, p) {
  if (!p) return 0
  if (p.total_lamports != null) return finiteNumber(p.total_lamports)
  if (p.totalLamports != null) return finiteNumber(p.totalLamports)
  const t = finiteNumber(p.token_amount ?? p.tokenAmount)
  const s = finiteNumber(p.spent_lamports ?? p.spentLamports)
  const r = finiteNumber(p.received_lamports ?? p.receivedLamports)
  const a = finiteNumber(c?.amount)
  const l = finiteNumber(c?.lamports)
  const price = a > 0 && l > 0 ? l / a : 0
  return r + t * price - s
}

export function walletPnlValue(w, p) {
  if (p === 'day') return finiteNumber(w.pnl_day ?? w.pnlDay ?? w.PnLDay)
  if (p === 'week') return finiteNumber(w.pnl_week ?? w.pnlWeek ?? w.PnLWeek)
  if (p === 'month') return finiteNumber(w.pnl_month ?? w.pnlMonth ?? w.PnLMonth)
  return finiteNumber(w.pnl_lamports ?? w.pnlLamports ?? w.PnLLamports)
}

export function walletPnlCostValue(w, p) {
  if (p === 'day') return finiteNumber(w.pnl_day_cost ?? w.pnlDayCost ?? w.PnLDayCost)
  if (p === 'week') return finiteNumber(w.pnl_week_cost ?? w.pnlWeekCost ?? w.PnLWeekCost)
  if (p === 'month') return finiteNumber(w.pnl_month_cost ?? w.pnlMonthCost ?? w.PnLMonthCost)
  return finiteNumber(w.pnl_cost ?? w.pnlCost ?? w.PnLCost)
}

function finiteNumber(v) {
  const n = Number(v || 0)
  return Number.isFinite(n) ? n : 0
}

function plainBridgeValue(value) {
  if (value == null || typeof value !== 'object') return value
  return JSON.parse(JSON.stringify(value))
}


function publicPositionLifecycles(items, wallet) {
  const rows = Array.isArray(items) ? items : []
  const scoped = wallet ? rows.filter(item => String(item?.wallet || '') === wallet) : rows
  return scoped.slice(0, 24).map(item => plainBridgeValue(item))
}

function isClosedPositionLifecycle(item) {
  const raw = item?.remaining_token_amount_raw ?? item?.remainingTokenAmountRaw
  if (raw != null && raw !== '') {
    const value = String(raw).trim()
    if (/^\d+$/.test(value)) return /^0+$/.test(value)
    const number = Number(value)
    if (Number.isFinite(number)) return number <= 0
  }
  return String(item?.status || '').trim().toLowerCase() === 'closed'
}

function publicClosedPositions(items) {
  const rows = Array.isArray(items) ? items : []
  return rows
    .filter(isClosedPositionLifecycle)
    .map(item => {
      const coin = item?.coin && typeof item.coin === 'object' ? item.coin : {}
      const entry = item?.entry && typeof item.entry === 'object' ? item.entry : {}
      const exit = item?.exit && typeof item.exit === 'object' ? item.exit : {}
      const spentLamports = finiteNumber(item?.spent_lamports)
      const receivedLamports = finiteNumber(item?.received_lamports)
      const entryAmountRaw = String(entry?.token_amount_raw ?? item?.token_amount_raw ?? '0')
      const exitAmountRaw = String(exit?.token_amount_raw ?? '0')
      const entryValueLamports = finiteNumber(entry?.value_lamports ?? spentLamports)
      const exitValueLamports = finiteNumber(exit?.value_lamports ?? receivedLamports)
      return {
        id: String(item?.id || ''),
        wallet: String(item?.wallet || ''),
        mint: String(item?.mint || ''),
        name: String(coin?.name || ''),
        symbol: String(coin?.symbol || ''),
        status: 'closed',
        token_amount_raw: String(item?.token_amount_raw || '0'),
        remaining_token_amount_raw: String(item?.remaining_token_amount_raw ?? item?.remainingTokenAmountRaw ?? '0'),
        spent_lamports: spentLamports,
        received_lamports: receivedLamports,
        realized_lamports: finiteNumber(item?.realized_lamports),
        total_lamports: finiteNumber(item?.total_lamports),
        pnl_bps: finiteNumber(item?.pnl_bps),
        entry_at: String(item?.entry_at || ''),
        closed_at: String(item?.exit_at || exit?.at || ''),
        duration_ms: finiteNumber(item?.duration_ms),
        entry_amount_raw: entryAmountRaw,
        entry_value_lamports: entryValueLamports,
        entry_price_lamports_per_token_raw: unitPrice(entryValueLamports, entryAmountRaw),
        exit_amount_raw: exitAmountRaw,
        exit_value_lamports: exitValueLamports,
        exit_price_lamports_per_token_raw: unitPrice(exitValueLamports, exitAmountRaw),
        entry_reason: String(entry?.reason || ''),
        exit_reason: String(exit?.reason || item?.exit_reason || ''),
        source: String(entry?.source || ''),
        source_id: String(entry?.source_id || ''),
        plugin_id: String(entry?.plugin_id || ''),
        plugin_name: String(entry?.plugin_name || ''),
        plugin_version: finiteNumber(entry?.plugin_version),
        entry_signature: String(entry?.signature || ''),
        exit_signature: String(exit?.signature || ''),
      }
    })
}

function unitPrice(valueLamports, amountRaw) {
  const amount = Number(amountRaw)
  if (!Number.isFinite(amount) || amount <= 0) return 0
  return finiteNumber(valueLamports) / amount
}

function publicMarketTrades(raw, visibleMintSet) {
 const rows=Array.isArray(raw)?raw:[]
 return rows.filter(item=>item && visibleMintSet.has(String(item.mint||'')) && ['buy','sell','add'].includes(String(item.method||'').toLowerCase()))
   .slice(0,64).map(item=>({
     signature:String(item.signature||''),
     mint:String(item.mint||''),
     owner:String(item.owner||''),
     method:String(item.method||'').toLowerCase(),
     slot:String(item.slot||''),
     amount:String(item.amount||''),
     lamports:String(item.lamports||''),
     created_at:String(item.created_at||''),
   }))
}
